import React, { useState, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Business as DepartmentIcon
} from '@mui/icons-material';
import { departmentService } from '../services/api';

const DepartmentSelector = ({
  value,
  onChange,
  label = "Select Department",
  placeholder = "Choose department...",
  disabled = false,
  required = false,
  helperText = "",
  error = false,
  excludeDepartmentId = null, // Exclude specific department (e.g., current department to prevent circular reference)
  size = 'small',
  sx = {}
}) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load departments
  const loadDepartments = async () => {
    try {
      setLoading(true);
      const allDepartmentsResponse = await departmentService.getAll();

      console.log('Department response:', allDepartmentsResponse); // Debug log

      let allDepartments = allDepartmentsResponse || [];
      
      // Exclude specific department if provided (prevent circular reference)
      if (excludeDepartmentId) {
        allDepartments = allDepartments.filter(dept => dept.id !== excludeDepartmentId);
      }
      
      console.log('Filtered departments:', allDepartments); // Debug log
      setDepartments(allDepartments);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  // Load departments on component mount
  useEffect(() => {
    loadDepartments();
  }, [excludeDepartmentId]);

  // Find selected department object - always return null if no match to keep it controlled
  const selectedDepartment = React.useMemo(() => {
    if (!value || value === '' || departments.length === 0) {
      return null;
    }
    return departments.find(dept => dept.id === value) || null;
  }, [value, departments]);

  // Generate department color based on code
  const getDepartmentColor = (code) => {
    const colors = [
      '#2196f3', '#4caf50', '#ff9800', '#9c27b0',
      '#f44336', '#00bcd4', '#8bc34a', '#ffc107',
      '#673ab7', '#03a9f4', '#cddc39', '#ff5722'
    ];
    const hash = code.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Autocomplete
      size={size}
      sx={{
        '& .MuiInputBase-root': { height: 40 },
        '& .MuiAutocomplete-input': { padding: '8px 12px' },
        '& .MuiAutocomplete-listbox': { maxHeight: '300px' },
        '& .MuiAutocomplete-option': { borderBottom: '1px solid #f0f0f0' },
        '& .MuiAutocomplete-option:last-child': { borderBottom: 'none' },
        ...sx,
      }}
      value={selectedDepartment}
      onChange={(_, newValue) => {
        onChange(newValue ? newValue.id : null);
      }}
      options={departments}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={loading}
      disableClearable={false}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          margin="none"
          size={size}
          sx={{
            '& .MuiInputBase-root': { height: 40 },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              bgcolor: getDepartmentColor(option.code),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 600,
              fontSize: '12px'
            }}
          >
            {option.code}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {option.name}
            </Typography>
            {option.description && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px'
                }}
              >
                {option.description.replace(/<[^>]*>/g, '')} {/* Strip HTML tags */}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label={`${option.employeeCount || 0} employees`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '10px', height: '20px' }}
              />
              {option.managerName && (
                <Chip
                  label={`Manager: ${option.managerName}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontSize: '10px', height: '20px' }}
                />
              )}
            </Box>
          </Box>
        </Box>
        );
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            variant="outlined"
            label={option.name}
            icon={
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '4px',
                  bgcolor: getDepartmentColor(option.code),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600
                }}
              >
                {option.code}
              </Box>
            }
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      noOptionsText={
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading departments...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <DepartmentIcon color="action" />
            <Typography variant="body2">No departments available</Typography>
          </Box>
        )
      }
    />
  );
};

export default DepartmentSelector;
